<template>
  <div
    v-if="element.type === 'placeholder'"
    class="placeholder-on-add-widget placeholder-on-add-widget--allowed m-4"
  />
  <div
    v-else
    :data-widget="JSON.stringify(element)"
    :data-location="JSON.stringify({ rowId: rowParentId, columnId: columnParentId, panelData: {
      panelId: panelId,
      subRowId: rowId,
      subColumnId: columnData.id,
      widgetIndex: index
    } })"
  >
    <div
      v-show="isChoosedForDrag"
    />
    <div
      v-show="!isChoosedForDrag"
      class="relative sb-subcol-item handle-subwidget"
      :class="{ 'sb-subcol-item--hovered': hovered || isActiveWidget }"
      :data-widget="JSON.stringify(element)"
      :data-location="JSON.stringify({ rowId: rowParentId, columnId: columnParentId, panelData: {
        panelId: panelId,
        subRowId: rowId,
        subColumnId: columnData.id,
        widgetIndex: index
      } })"
      @mouseover="onMouseOver"
      @mouseleave="onMouseLeave"
      @click.stop="editWidget"
    >
      <div
        v-if="hovered"
        class="sb-subcol-title"
      >
        <span>{{ t('statsEditor.widget') }}</span>
      </div>
      <div
        class="sb-subcol-actions z-2"
        :class="{ 'is-visible': hovered }"
      >
        <div class="flex flex-column">
          <div
            v-tooltip.right="t('statsEditor.actions.tooltip.widget.remove')"
            class="flex sb-action mb-1"
            @click.stop="removeWidget"
          >
            <i class="far fa-trash" />
          </div>
        </div>
      </div>
      <StatsWidgetDataRender
        v-if="element.type === 'StatsWidgetData'"
        :widget-data="element"
      />
      <StatsWidgetTitleRender
        v-else-if="element.type === 'StatsWidgetTitle'"
        :widget-data="element"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, Ref, SetupContext, ref,
} from 'vue';

import Tooltip from 'primevue/tooltip';

import StatsWidgetDataRender from '@/components/stats-builder/widgets/render/StatsWidgetDataRender.vue';
import StatsWidgetTitleRender from '@/components/stats-builder/widgets/render/StatsWidgetTitleRender.vue';
import { StatsBuilderSelectedWidget, StatsBuilderChoosedWidgetForDrag } from '@/types/stats-editor-types';
import { useStore } from '@/store';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'StatsBuilderSubColItem',

  components: {
    StatsWidgetDataRender,
    StatsWidgetTitleRender,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    element: {
      type: Object,
      required: true,
    },

    index: {
      type: Number,
      required: true,
    },

    rowId: {
      type: String,
      required: true,
    },

    columnData: {
      type: Object,
      required: true,
    },

    rowParentId: {
      type: String,
      required: true,
    },

    columnParentId: {
      type: String,
      required: true,
    },

    panelId: {
      type: String,
      required: true,
    },
  },

  emits: ['edit-widget', 'remove-widget'],

  setup(props, { emit }: SetupContext) {
    const { t } = useI18n();
    const store = useStore();
    const selectedWidgetState: Ref<StatsBuilderSelectedWidget | null> = computed(
      () => store.getters['statsEditor/getSelectedWidget'],
    );

    const hovered = ref(false);

    const panelDataHasWidgetIndex = (
      panelData: StatsBuilderSelectedWidget['location']['panelData'],
    ): panelData is NonNullable<StatsBuilderSelectedWidget['location']['panelData']> & { widgetIndex: number } => (
      !!panelData && typeof (panelData as Record<string, unknown>).widgetIndex === 'number'
    );

    const isActiveWidget = computed(() => {
      if (!selectedWidgetState.value) {
        return false;
      }
      const { location } = selectedWidgetState.value;
      if (location.rowId !== props.rowParentId || location.columnId !== props.columnParentId) {
        return false;
      }
      if (!panelDataHasWidgetIndex(location.panelData)) {
        return false;
      }
      const { panelData } = location;
      return panelData.panelId === props.panelId
        && panelData.subRowId === props.rowId
        && panelData.subColumnId === props.columnData.id
        && panelData.widgetIndex === props.index;
    });

    const choosedWidgetForDrag = computed<StatsBuilderChoosedWidgetForDrag | null>(() => store.getters['statsEditor/getChoosedWidgetForDrag']);

    const isChoosedForDrag = computed(() => {
      if (!choosedWidgetForDrag.value) {
        return false;
      }
      const continueCheck = choosedWidgetForDrag.value.rowId === props.rowParentId
        && choosedWidgetForDrag.value.columnId === props.columnParentId && choosedWidgetForDrag.value.index === props.index;
      if (continueCheck && panelDataHasWidgetIndex(choosedWidgetForDrag.value.panelData)) {
        const { panelData } = choosedWidgetForDrag.value;
        return panelData.panelId === props.panelId && panelData.subRowId === props.rowId
          && panelData.subColumnId === props.columnData.id && panelData.widgetIndex === props.index;
      }
      return false;
    });

    const editWidget = () => {
      emit('edit-widget', props.index);
    };

    const removeWidget = () => {
      emit('remove-widget', props.index);
    };

    const onMouseOver = () => {
      hovered.value = true;
    };
    const onMouseLeave = () => {
      hovered.value = false;
    };

    return {
      t,
      selectedWidgetState,
      hovered,
      isChoosedForDrag,
      isActiveWidget,

      editWidget,
      removeWidget,
      onMouseOver,
      onMouseLeave,
    };
  },
});
</script>

<style lang="scss">
  $brand-green: #94c840;
  $brand-green-dark: #36520f;

  #pageRowsList .sb-subcol-item {
    position: relative;
    border: 1px solid rgba(73, 80, 87, 0.12);
    background: linear-gradient(135deg, #ffffff 0%, #f8faf4 100%);
    min-height: 4.5rem;
    border-radius: 0.75rem;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, background 0.2s ease;
  }

  #pageRowsList .sb-subcol-item--hovered {
    border-color: rgba($brand-green, 0.85);
    box-shadow: 0 16px 32px -26px rgba($brand-green-dark, 0.45), 0 0 0 1px rgba($brand-green, 0.4);
    background: linear-gradient(135deg, rgba($brand-green, 0.14), rgba($brand-green, 0.04));
    transform: translateY(-2px);
  }

  #pageRowsList .sb-subcol-actions {
    position: absolute;
    top: 0.55rem;
    left: 0.85rem;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.18s ease;

    &.is-visible {
      opacity: 1;
      pointer-events: auto;
    }

    .sb-action {
      cursor: pointer;
      padding: 0.5rem;
      background: rgba(255, 255, 255, 0.98);
      border: 1px solid rgba($brand-green-dark, 0.28);
      border-radius: 0.6rem;
      box-shadow: 0 14px 28px -22px rgba($brand-green-dark, 0.45);
      transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, color 0.2s ease, border-color 0.2s ease;
      color: #2f3b1a;
    }

    .sb-action:hover {
      background: #ffffff;
      border-color: rgba($brand-green-dark, 0.5);
      box-shadow: 0 18px 34px -20px rgba($brand-green-dark, 0.5);
      transform: translateY(-1px);
      color: #2f3b1a;
    }
  }

  #pageRowsList .sb-subcol-title {
    position: absolute;
    top: -1.2rem;
    left: -0.1rem;
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.8rem 0.3rem;
    border-radius: 999px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(231, 247, 206, 0.92));
    border: 1px solid rgba($brand-green, 0.45);
    color: #314911;
    font-size: 0.72rem;
    font-weight: 650;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    line-height: 1rem;
    box-shadow: 0 10px 22px -18px rgba($brand-green-dark, 0.42);
    z-index: 2;
  }
</style>
